<script setup>
/* 术语标记（显式版）。三种用法：
 *
 *   <TermTip term="slippage" />              渲染词典里的标准名
 *   <TermTip term="ratchet">保底</TermTip>   自定义显示文案
 *   <TermTip :text="row.name" />             扫一段文本，自动标出其中的术语
 *
 * 第三种是给**动态文本**准备的。自动扫描（utils/glossaryScan.js）只敢碰
 * 静态标签，因为直接改 Vue 管的 DOM 会和它的更新打架；这里换个思路 ——
 * 由 Vue 自己把 span 渲染出来，就没有"谁改谁"的问题，每 2 秒刷新的
 * 单元格也照样用。
 *
 * 匹配逻辑与自动扫描同源，不另写一份。
 */
import { computed } from 'vue'
import { GLOSSARY } from '@/glossary'
import { findMatches } from '@/utils/glossaryScan'

const props = defineProps({
  /** 词典 key。与 text 二选一。 */
  term: { type: String, default: '' },
  /** 任意文本，自动标出其中的术语。与 term 二选一。 */
  text: { type: String, default: '' },
})

const entry = computed(() => (props.term ? GLOSSARY[props.term] : null))

/** 把文本切成 {text} 与 {text, key} 的混合序列，交给模板渲染。 */
const parts = computed(() => {
  const t = props.text
  if (!t) return []
  const out = []
  let cursor = 0
  for (const h of findMatches(t)) {
    if (h.start > cursor) out.push({ text: t.slice(cursor, h.start) })
    out.push({ text: t.slice(h.start, h.end), key: h.key })
    cursor = h.end
  }
  if (cursor < t.length) out.push({ text: t.slice(cursor) })
  return out
})
</script>

<template>
  <!-- term 模式下 slot 优先：允许显示文案与词典标准名不同 -->
  <span v-if="term && $slots.default" class="gloss" :data-term="term" tabindex="0">
    <slot />
  </span>
  <span v-else-if="term && entry" class="gloss" :data-term="term" tabindex="0">
    {{ entry.term }}
  </span>
  <template v-else-if="text">
    <template v-for="(p, i) in parts" :key="i">
      <span v-if="p.key" class="gloss" :data-term="p.key" tabindex="0">{{ p.text }}</span>
      <template v-else>{{ p.text }}</template>
    </template>
  </template>
  <!-- term 给了但词典里没有：原样显示，不要静默吞掉 -->
  <template v-else>{{ term }}</template>
</template>
